import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { aiAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Sparkles, Copy, Check, Wand2 } from 'lucide-react';

const promptTemplates = {
  product: 'Write a compelling product description for a health supplement that promotes vitality and energy. Focus on benefits, not medical claims.',
  seo: 'Generate an SEO-optimized meta title and description for a supplement sales page. Include relevant keywords.',
  review: 'Create a realistic customer testimonial for a health supplement. Include name, location, and specific positive experience.',
  headline: 'Write 5 attention-grabbing headlines for a supplement landing page. Focus on urgency and benefits.',
  faq: 'Create 5 FAQ questions and answers for a supplement product page. Address common concerns.',
  cta: 'Write 3 different call-to-action button texts and supporting copy for a supplement checkout page.',
};

export const AdminAI = () => {
  const [prompt, setPrompt] = useState('');
  const [contentType, setContentType] = useState('general');
  const [result, setResult] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    setResult('');
    try {
      const response = await aiAPI.generate({ prompt, content_type: contentType });
      setResult(response.data.content);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate content');
    } finally {
      setLoading(false);
    }
  };

  const copyResult = () => {
    navigator.clipboard.writeText(result);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const applyTemplate = (key) => {
    setPrompt(promptTemplates[key]);
    setContentType(key === 'seo' ? 'seo' : key === 'review' ? 'review' : key === 'product' ? 'product' : 'general');
  };

  return (
    <div className="space-y-8" data-testid="admin-ai">
      <div>
        <h1 className="font-heading text-3xl font-bold text-slate-900">AI Content Generator</h1>
        <p className="mt-2 text-slate-600">Use Mistral AI to generate compelling copy</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Input */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wand2 className="w-5 h-5" />
              Generate Content
            </CardTitle>
            <CardDescription>Describe what content you need</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Content Type</Label>
              <Select value={contentType} onValueChange={setContentType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General Copy</SelectItem>
                  <SelectItem value="seo">SEO Meta Content</SelectItem>
                  <SelectItem value="product">Product Description</SelectItem>
                  <SelectItem value="review">Customer Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Your Prompt</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Describe the content you want to generate..."
                rows={6}
                data-testid="ai-prompt-input"
              />
            </div>
            <Button
              onClick={handleGenerate}
              disabled={loading || !prompt.trim()}
              className="w-full"
              data-testid="ai-generate-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                  Generating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Generate Content
                </span>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Templates */}
        <Card>
          <CardHeader>
            <CardTitle>Quick Templates</CardTitle>
            <CardDescription>Click to use a template</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(promptTemplates).map(([key, value]) => (
              <Button
                key={key}
                variant="outline"
                className="w-full justify-start text-left h-auto py-3"
                onClick={() => applyTemplate(key)}
              >
                <div>
                  <p className="font-medium capitalize">{key}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{value.substring(0, 50)}...</p>
                </div>
              </Button>
            ))}
          </CardContent>
        </Card>

        {/* Result */}
        {result && (
          <Card className="lg:col-span-3">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  Generated Content
                </CardTitle>
                <Button variant="outline" size="sm" onClick={copyResult}>
                  {copied ? (
                    <Check className="w-4 h-4 mr-1" />
                  ) : (
                    <Copy className="w-4 h-4 mr-1" />
                  )}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-50 rounded-lg p-4 whitespace-pre-wrap text-slate-700">
                {result}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminAI;
